import { apiClient } from './client';
import type {
  Account,
  AccountsResponse,
  AccountBalance,
  TransactionsResponse,
  TransactionFilters,
  UserPreferences,
} from '../../types/account';

/**
 * Account Service - Handles all account-related API operations
 * Based on Trading API v1.1.0 specification
 */
export class AccountService {
  /**
   * Fetch all accessible accounts for the authenticated user
   * GET /v1/accounts
   */
  async getAccounts(): Promise<AccountsResponse> {
    return apiClient.get<AccountsResponse>('/v1/accounts');
  }

  /**
   * Fetch detailed information for a specific account
   * GET /v1/accounts/{account_id}
   */
  async getAccountDetails(accountId: string): Promise<Account> {
    return apiClient.get<Account>(`/v1/accounts/${accountId}`);
  }

  /**
   * Fetch current balance for a specific account
   * GET /v1/accounts/{account_id}/balance
   */
  async getAccountBalance(accountId: string): Promise<AccountBalance> {
    return apiClient.get<AccountBalance>(`/v1/accounts/${accountId}/balance`);
  }

  /**
   * Fetch transaction history for a specific account
   * GET /v1/accounts/{account_id}/transactions
   */
  async getAccountTransactions(filters: TransactionFilters): Promise<TransactionsResponse> {
    const params = new URLSearchParams();
    
    // Add required accountId as query parameter
    params.append('accountId', filters.accountId);
    
    // Add optional filters
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.type) params.append('type', filters.type);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const endpoint = `/v1/accounts/${filters.accountId}/transactions?${params.toString()}`;
    return apiClient.get<TransactionsResponse>(endpoint);
  }

  /**
   * Fetch user preferences
   * GET /v1/preferences
   */
  async getUserPreferences(): Promise<UserPreferences> {
    return apiClient.get<UserPreferences>('/v1/preferences');
  }

  /**
   * Update user preferences
   * PUT /v1/preferences
   */
  async updateUserPreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
    return apiClient.put<UserPreferences>('/v1/preferences', preferences);
  }
}

// Export singleton instance
export const accountService = new AccountService();

/**
 * Token Management Utilities
 */
export class TokenManager {
  private static readonly TOKEN_KEY = 'trading_api_token';
  private static readonly ACCOUNT_DATA_KEY = 'trading_account_data';
  private static readonly TOKEN_EXPIRY_BUFFER = 5 * 60 * 1000; // 5 minutes buffer

  /**
   * Set authentication token and configure API client
   */
  static setToken(token: string): void {
    try {
      localStorage.setItem(this.TOKEN_KEY, token);
      apiClient.setToken(token);
    } catch (error) {
      console.error('Failed to store authentication token:', error);
      // Fallback to session storage
      try {
        sessionStorage.setItem(this.TOKEN_KEY, token);
        apiClient.setToken(token);
      } catch (sessionError) {
        console.error('Failed to store token in session storage:', sessionError);
        // Still set in API client for current session
        apiClient.setToken(token);
      }
    }
  }

  /**
   * Get stored authentication token
   */
  static getToken(): string | null {
    try {
      return localStorage.getItem(this.TOKEN_KEY) || sessionStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      console.error('Failed to retrieve authentication token:', error);
      return null;
    }
  }

  /**
   * Remove authentication token and clear API client
   */
  static clearToken(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.TOKEN_KEY);
      this.clearAccountData();
      apiClient.setToken(null);
    } catch (error) {
      console.error('Failed to clear authentication token:', error);
      // Still clear from API client
      apiClient.setToken(null);
    }
  }

  /**
   * Check if token exists and is not expired
   */
  static isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      // Basic JWT token structure check
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      // Decode payload to check expiry
      const payload = JSON.parse(atob(parts[1]));
      const expiryTime = payload.exp;

      // Check if token expires within buffer time
      if (expiryTime && (expiryTime * 1000 - Date.now()) < this.TOKEN_EXPIRY_BUFFER) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to validate token:', error);
      return false;
    }
  }

  /**
   * Get token expiry time
   */
  static getTokenExpiry(): number | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = JSON.parse(atob(parts[1]));
      return payload.exp ? payload.exp * 1000 : null;
    } catch (error) {
      console.error('Failed to get token expiry:', error);
      return null;
    }
  }

  /**
   * Store account data for quick access
   */
  static setAccountData(accounts: Account[], primaryAccountId: string): void {
    try {
      const accountData = {
        accounts,
        primaryAccountId,
        lastUpdate: Date.now(),
      };
      localStorage.setItem(this.ACCOUNT_DATA_KEY, JSON.stringify(accountData));
    } catch (error) {
      console.error('Failed to store account data:', error);
    }
  }

  /**
   * Get stored account data
   */
  static getAccountData(): { accounts: Account[]; primaryAccountId: string } | null {
    try {
      const data = localStorage.getItem(this.ACCOUNT_DATA_KEY);
      if (!data) return null;

      const parsed = JSON.parse(data);
      
      // Check if data is not too old (24 hours)
      if (Date.now() - parsed.lastUpdate > 24 * 60 * 60 * 1000) {
        this.clearAccountData();
        return null;
      }

      return {
        accounts: parsed.accounts,
        primaryAccountId: parsed.primaryAccountId,
      };
    } catch (error) {
      console.error('Failed to retrieve account data:', error);
      return null;
    }
  }

  /**
   * Clear stored account data
   */
  static clearAccountData(): void {
    try {
      localStorage.removeItem(this.ACCOUNT_DATA_KEY);
    } catch (error) {
      console.error('Failed to clear account data:', error);
    }
  }

  /**
   * Initialize token from storage and configure API client
   */
  static initialize(): boolean {
    const token = this.getToken();
    if (token && this.isTokenValid()) {
      apiClient.setToken(token);
      return true;
    } else if (token) {
      // Token exists but is invalid, clear it
      this.clearToken();
    }
    return false;
  }
}

/**
 * Session Management Utilities
 */
export class SessionManager {
  private static sessionTimeoutWarning: number | null = null;
  private static sessionTimeoutLogout: number | null = null;
  
  /**
   * Start session monitoring with automatic logout
   */
  static startSessionMonitoring(
    onWarning: () => void,
    onExpiry: () => void,
    warningMinutes = 5
  ): void {
    this.clearSessionTimers();

    const expiry = TokenManager.getTokenExpiry();
    if (!expiry) return;

    const now = Date.now();
    const timeToWarning = expiry - now - (warningMinutes * 60 * 1000);
    const timeToExpiry = expiry - now;

    if (timeToWarning > 0) {
      this.sessionTimeoutWarning = window.setTimeout(() => {
        onWarning();
      }, timeToWarning);
    }

    if (timeToExpiry > 0) {
      this.sessionTimeoutLogout = window.setTimeout(() => {
        this.logout();
        onExpiry();
      }, timeToExpiry);
    }
  }

  /**
   * Clear session monitoring timers
   */
  static clearSessionTimers(): void {
    if (this.sessionTimeoutWarning) {
      clearTimeout(this.sessionTimeoutWarning);
      this.sessionTimeoutWarning = null;
    }
    if (this.sessionTimeoutLogout) {
      clearTimeout(this.sessionTimeoutLogout);
      this.sessionTimeoutLogout = null;
    }
  }

  /**
   * Extend session (if supported by backend)
   */
  static async extendSession(): Promise<boolean> {
    try {
      // The API doesn't specify a session extension endpoint
      // This would typically call the brand API to refresh the token
      // For now, we'll just validate the current token
      return TokenManager.isTokenValid();
    } catch (error) {
      console.error('Failed to extend session:', error);
      return false;
    }
  }

  /**
   * Logout and clear all session data
   */
  static logout(): void {
    this.clearSessionTimers();
    TokenManager.clearToken();
    
    // Additional cleanup could be done here (clear other stores, etc.)
  }

  /**
   * Get time remaining until session expires (in milliseconds)
   */
  static getTimeUntilExpiry(): number | null {
    const expiry = TokenManager.getTokenExpiry();
    if (!expiry) return null;
    
    return Math.max(0, expiry - Date.now());
  }
}
